import { useState, type ReactNode } from "react";
import { ChevronDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { PermissionButton } from "@/components/ui/permission-button";
import { type BenefitSectionDef, getPlanTypeAccent } from "@shared/schemas/planTypes";
import { getTypeIcon } from "./typeIcons";

interface BenefitSectionProps {
  def: BenefitSectionDef;
  /** Status counts surfaced in the section header. */
  counts: { enrolled: number; pending: number; waived: number; unconfigured: number };
  /** Force-hide the per-section "Configure" CTA (e.g., when user lacks permission — PermissionButton handles its own hiding too). */
  canConfigure?: boolean;
  /** When true and at least one row is rendered, expand by default. Sections without `def.collapsible` are never collapsible. */
  defaultOpen?: boolean;
  /** Called when the user clicks the section-level "Configure {type}" CTA. */
  onConfigureSection?: () => void;
  children: ReactNode;
}

export function BenefitSection({
  def,
  counts,
  canConfigure = true,
  defaultOpen = true,
  onConfigureSection,
  children,
}: BenefitSectionProps) {
  const Icon = getTypeIcon(def.types[0]);
  const accent = getPlanTypeAccent(def.types[0]);
  const [open, setOpen] = useState(defaultOpen);
  const allZero = counts.enrolled + counts.pending + counts.waived + counts.unconfigured === 0;
  const showConfigureCta = canConfigure && counts.unconfigured > 0 && !!onConfigureSection;

  // Build the status line; hide zero segments.
  const segments: string[] = [];
  if (counts.enrolled > 0) segments.push(`${counts.enrolled} enrolled`);
  if (counts.pending > 0) segments.push(`${counts.pending} pending`);
  if (counts.waived > 0) segments.push(`${counts.waived} waived`);
  if (counts.unconfigured > 0) segments.push(`${counts.unconfigured} not configured`);
  const statusLine = segments.length === 0 ? "Nothing configured" : segments.join(" · ");

  const header = (
    <div className="flex items-center gap-3 w-full">
      <div className={cn("h-7 w-7 rounded-md flex items-center justify-center shrink-0", accent.accentBg)}>
        <Icon className={cn("h-4 w-4", accent.accentText)} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[15px] font-semibold text-stone-900 dark:text-stone-100 tracking-tight">
          {def.title}
        </div>
        <div className="text-[11.5px] text-stone-500 dark:text-stone-400">{statusLine}</div>
      </div>
      {showConfigureCta && (
        <PermissionButton
          permission="clients:edit"
          whenDenied="hide"
          variant="ghost"
          size="sm"
          className="gap-1.5 text-xs"
          onClick={(e: any) => {
            e?.stopPropagation?.();
            onConfigureSection?.();
          }}
        >
          <Plus className="h-3.5 w-3.5" />
          Configure {def.title.toLowerCase()}
        </PermissionButton>
      )}
      {def.collapsible && (
        <ChevronDown
          className={cn(
            "h-4 w-4 text-stone-500 transition-transform shrink-0",
            open ? "rotate-180" : "rotate-0",
          )}
        />
      )}
    </div>
  );

  const body = <div className="space-y-2 pt-2">{children}</div>;

  if (def.collapsible) {
    return (
      <Collapsible open={open} onOpenChange={setOpen} className={cn(allZero && "opacity-90")}>
        <CollapsibleTrigger asChild>
          <button type="button" className="flex w-full items-center text-left rounded-md hover:bg-stone-50 dark:hover:bg-stone-900/40 transition-colors px-1 py-1">
            {header}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>{body}</CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <div className={cn(allZero && "opacity-90")}>
      <div className="px-1 py-1">{header}</div>
      {body}
    </div>
  );
}
